import { getCsrfToken } from "@/app/_utils/csrf";

export type AdminUserStatus = "pending" | "active" | "suspended";

export interface Role {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  permissions: number[];
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: number;
  codename: string;
  label: string;
  kind: "page" | "action";
}

export interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: AdminUserStatus;
  roles: Role[];
  invited_by_email: string | null;
  invited_at: string | null;
  last_login_at: string | null;
  created_at: string;
}

export interface InvitePayload {
  email: string;
  first_name?: string;
  last_name?: string;
  role_ids: number[];
}

export interface RolePayload {
  name: string;
  description?: string;
  permissions: number[];
}

class RbacService {
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}rbac/`;

  private get csrf() {
    return getCsrfToken();
  }

  /** Throws on non-2xx so callers surface the real message (e.g. "last Super Admin"). */
  private async handle(res: Response) {
    if (res.status === 204) return null;
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(errorMessage(body) ?? `Request failed (${res.status})`);
    return body;
  }

  private write(url: string, method: string, data?: unknown) {
    return fetch(url, {
      method,
      body: data === undefined ? undefined : JSON.stringify(data),
      headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf },
      credentials: "include",
    });
  }

  getUsersAsync = async (): Promise<AdminUser[]> =>
    this.handle(
      await fetch(`${this.baseUrl}users/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
    );

  inviteUserAsync = async (data: InvitePayload): Promise<AdminUser> =>
    this.handle(await this.write(`${this.baseUrl}users/`, "POST", data));

  updateUserRolesAsync = async (id: number, roleIds: number[]): Promise<AdminUser> =>
    this.handle(
      await this.write(`${this.baseUrl}users/${id}/`, "PATCH", { role_ids: roleIds })
    );

  resendInviteAsync = async (id: number): Promise<AdminUser> =>
    this.handle(await this.write(`${this.baseUrl}users/${id}/resend/`, "POST"));

  suspendUserAsync = async (id: number): Promise<AdminUser> =>
    this.handle(await this.write(`${this.baseUrl}users/${id}/suspend/`, "POST"));

  reactivateUserAsync = async (id: number): Promise<AdminUser> =>
    this.handle(await this.write(`${this.baseUrl}users/${id}/reactivate/`, "POST"));

  deleteUserAsync = async (id: number): Promise<null> =>
    this.handle(await this.write(`${this.baseUrl}users/${id}/`, "DELETE"));

  getRolesAsync = async (): Promise<Role[]> =>
    this.handle(
      await fetch(`${this.baseUrl}roles/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
    );

  getPermissionsAsync = async (): Promise<Permission[]> =>
    this.handle(
      await fetch(`${this.baseUrl}permissions/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
    );

  createRoleAsync = async (data: RolePayload): Promise<Role> =>
    this.handle(await this.write(`${this.baseUrl}roles/`, "POST", data));

  updateRoleAsync = async (id: number, data: Partial<RolePayload>): Promise<Role> =>
    this.handle(await this.write(`${this.baseUrl}roles/${id}/`, "PATCH", data));

  deleteRoleAsync = async (id: number): Promise<null> =>
    this.handle(await this.write(`${this.baseUrl}roles/${id}/`, "DELETE"));
}

/** Pulls a readable message out of a DRF error body, e.g. `{ email: ["..."] }`. */
function errorMessage(body: any): string | null {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (typeof body.error === "string") return body.error;
  if (typeof body.detail === "string") return body.detail;
  const first = Object.values(body)[0];
  if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  return null;
}

export default new RbacService();
